import { GetMaterializedApprovalsFlowsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useBulkApproveMaterializedApprovalFlow,
  useBulkRejectMaterializedApprovalFlow,
} from 'modules/approvals/hooks/useMaterializedApprovalFlows'
import { APRROVER_STATUS } from 'modules/approvals/lib/roles'

type IBulkApproveRejectFlows = {
  accessId: string
  reason?: string
  updateWholeFlow?: boolean
} & (
  | {
      materializedFlowId: string
    }
  | {
      materializedFlowIds: string[]
    }
)

const useBulkApproveRejectFlows = () => {
  const [fetchMaterializedApprovalsFlows] = useLazyQuery(GetMaterializedApprovalsFlowsDocument)
  const setFlashMessage = (message: string, type: 'correct' | 'wrong') => {
    app.setFlash({
      type,
      message,
      time: 2000,
    })
  }

  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const { loading: approving, bulkApproveMaterializedFlow } =
    useBulkApproveMaterializedApprovalFlow()
  const { loading: rejecting, bulkRejectMaterializedFlow } = useBulkRejectMaterializedApprovalFlow()

  const fetchMultipleMaterializedApprovalFlows = async (materializedFlowIds: string[]) => {
    const { data, error } = await fetchMaterializedApprovalsFlows({
      query: GetMaterializedApprovalsFlowsDocument,
      variables: { ids: materializedFlowIds, companyId: currentCompanyId },
    })

    return {
      materializedApprovalsFlows: data?.approvals.materializedApprovalsFlowsConnection.nodes,
      error,
    }
  }

  const currentMaterializedApprovers = async (
    materializedFlowIds: string[],
    currentAccessId: string
  ) => {
    const { materializedApprovalsFlows, error } =
      await fetchMultipleMaterializedApprovalFlows(materializedFlowIds)

    if (error) {
      throw error
    }

    return materializedFlowIds
      .map((flowId) => {
        const flow = materializedApprovalsFlows?.find((f) => f.id === flowId)

        if (!flow) return

        const flowApprover = flow.steps
          .flatMap((step) => step.stepApprovers)
          .find(
            (approver) =>
              approver.access?.id === currentAccessId && approver.status === APRROVER_STATUS.pending
          )

        if (!flowApprover) return

        return {
          materializedFlowId: flowId,
          approverId: flowApprover.id,
        }
      })
      .filter(Boolean)
  }

  type Callback = () => void | Promise<void>

  // Resolves the flow and step-approver IDs to pass to the mutation.
  // When updateWholeFlow is true the caller is a global approver (e.g. admin not in any
  // step). The backend uses override_approver_id from the access record, so we do NOT need
  // to look up step-approver IDs — doing so would cause an empty-array early-return for
  // admins who are not assigned to any flow step (FCT-50505).
  //
  // When the step-approver lookup succeeds but returns no matching approvers (e.g. the user
  // is a global approver but not an explicit step approver), we fall back to the
  // updateWholeFlow path and let the backend validate permissions instead of blocking with
  // a client-side error (FCT-49779).
  //
  // When the lookup fails due to a query/network error, we surface the error flash and
  // return null so the mutation is not attempted with incomplete data.
  const resolveApproversAndFlowIds = async (
    actionData: IBulkApproveRejectFlows,
    onError?: Callback
  ): Promise<{
    updateWholeFlow: boolean
    materializedStepApproverIds: string[]
    materializedApprovalFlowIds: string[]
  } | null> => {
    const flowIds =
      'materializedFlowId' in actionData
        ? [actionData.materializedFlowId]
        : actionData.materializedFlowIds

    if (actionData.updateWholeFlow) {
      return {
        updateWholeFlow: true,
        materializedStepApproverIds: [],
        materializedApprovalFlowIds: flowIds,
      }
    }

    let approversAndFlows: Awaited<ReturnType<typeof currentMaterializedApprovers>>
    try {
      approversAndFlows = await currentMaterializedApprovers(flowIds, actionData.accessId)
    } catch {
      // Query/network error — show error and abort so we don't proceed with stale data.
      await onError?.()
      setFlashMessage(
        i18n.t('settings.approvals.approval_flow.flash_messages.error_update'),
        'wrong'
      )
      return null
    }

    if (!approversAndFlows?.length) {
      // The user is not a step approver on any of the requested flows. Fall back to the
      // whole-flow path so the backend can check whether they are a global approver.
      return {
        updateWholeFlow: true,
        materializedStepApproverIds: [],
        materializedApprovalFlowIds: flowIds,
      }
    }

    return {
      updateWholeFlow: false,
      materializedStepApproverIds: approversAndFlows.map((it) => it.approverId),
      materializedApprovalFlowIds: approversAndFlows.map((it) => it.materializedFlowId),
    }
  }

  const approve = async (
    actionData: IBulkApproveRejectFlows,
    onSuccess?: Callback,
    onError?: Callback,
    showSuccessMessage = true
  ) => {
    const resolved = await resolveApproversAndFlowIds(actionData, onError)
    if (!resolved) return

    const variables = {
      updateWholeFlow: resolved.updateWholeFlow,
      reason: actionData.reason,
      accessId: actionData.accessId,
      materializedStepApproverIds: resolved.materializedStepApproverIds,
      materializedApprovalFlowIds: resolved.materializedApprovalFlowIds,
      source: 'web',
    }

    const result = await bulkApproveMaterializedFlow(variables, currentCompanyId)
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      await onSuccess?.()
      if (showSuccessMessage) {
        setFlashMessage(
          i18n.t('settings.approvals.approval_action.flash_messages.approve'),
          'correct'
        )
      }
    } else {
      await onError?.()
      const isForbidden = handled.errors.some(
        (e) => e.__typename === 'SimpleError' && 'type' in e && e.type === 'forbidden'
      )
      if (isForbidden) {
        // Override the generic "Forbidden access" flash from handleResult with a descriptive message
        setFlashMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.error_forbidden'),
          'wrong'
        )
      } else {
        const error = handled.errors[0]
        const message =
          error && 'message' in error
            ? error.message
            : i18n.t('settings.approvals.approval_flow.flash_messages.error_update')
        setFlashMessage(message, 'wrong')
      }
    }
  }

  const reject = async (
    actionData: IBulkApproveRejectFlows,
    onSuccess?: Callback,
    onError?: Callback,
    showSuccessMessage = true
  ) => {
    const resolved = await resolveApproversAndFlowIds(actionData, onError)
    if (!resolved) return

    const variables = {
      updateWholeFlow: resolved.updateWholeFlow,
      reason: actionData.reason,
      accessId: actionData.accessId,
      materializedStepApproverIds: resolved.materializedStepApproverIds,
      materializedApprovalFlowIds: resolved.materializedApprovalFlowIds,
      source: 'web',
    }

    const result = await bulkRejectMaterializedFlow(variables, currentCompanyId)
    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      await onSuccess?.()
      if (showSuccessMessage) {
        setFlashMessage(
          i18n.t('settings.approvals.approval_action.flash_messages.reject'),
          'correct'
        )
      }
    } else {
      await onError?.()
      const isForbidden = handled.errors.some(
        (e) => e.__typename === 'SimpleError' && 'type' in e && e.type === 'forbidden'
      )
      if (isForbidden) {
        // Override the generic "Forbidden access" flash from handleResult with a descriptive message
        setFlashMessage(
          i18n.t('settings.approvals.approval_flow.flash_messages.error_forbidden'),
          'wrong'
        )
      } else {
        const error = handled.errors[0]
        const message =
          error && 'message' in error
            ? error.message
            : i18n.t('settings.approvals.approval_flow.flash_messages.error_update')
        setFlashMessage(message, 'wrong')
      }
    }
  }

  return {
    disabled: approving || rejecting,
    approve,
    reject,
  }
}

export default useBulkApproveRejectFlows
